import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Crown, X, Sparkles, Lock } from 'lucide-react';
import { trackUpsellShown, trackUpsellClicked } from '../utils/analytics';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 360px;
  overflow: hidden;
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  padding: 32px 24px 28px;
  text-align: center;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  
  &:hover { background: rgba(255, 255, 255, 0.3); }
`;

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  
  svg { width: 36px; height: 36px; color: white; }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: white;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`;

const Body = styled.div`
  padding: 24px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #475569;
  
  svg {
    width: 20px;
    height: 20px;
    color: #FFA929;
    flex-shrink: 0;
  }
`;

const UpgradeBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(255, 169, 41, 0.4);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 169, 41, 0.5);
  }
  
  &:active { transform: scale(0.98); }
`;

const LaterBtn = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  color: #94a3b8;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  
  &:hover { color: #64748b; }
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
}

export default function PremiumUpgradeModal({ isOpen, onClose, featureName = 'fitur ini' }: Props) {
  const navigate = useNavigate();
  
  // Track when upsell modal is shown
  useEffect(() => {
    if (isOpen) {
      trackUpsellShown(featureName);
    }
  }, [isOpen, featureName]);

  if (!isOpen) return null;
  
  const handleUpgrade = () => {
    trackUpsellClicked(featureName);
    onClose();
    navigate('/premium');
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <CloseBtn onClick={onClose}>
            <X size={18} />
          </CloseBtn>
          <IconCircle>
            <Lock />
          </IconCircle>
          <Title>Konten Premium</Title>
          <Subtitle>Upgrade untuk akses {featureName}</Subtitle>
        </Header>
        
        <Body>
          <FeatureList>
            <Feature>
              <Sparkles />
              Akses semua game premium tanpa batas
            </Feature>
            <Feature>
              <Sparkles />
              Fitur tumbuh kembang lengkap
            </Feature>
            <Feature>
              <Sparkles />
              Bebas iklan selamanya
            </Feature>
            <Feature>
              <Sparkles />
              Laporan perkembangan anak
            </Feature>
          </FeatureList>
          
          <UpgradeBtn onClick={handleUpgrade}>
            <Crown size={20} />
            Upgrade ke Premium
          </UpgradeBtn>
          
          <LaterBtn onClick={onClose}>
            Nanti saja
          </LaterBtn>
        </Body>
      </Modal>
    </Overlay>
  );
}
